import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type CustomerStatus } from '../../lib/adminApi.js';
import { Icon } from '../../components/Icon.js';
import { fmt } from '../../lib/format.js';

// Customers list — every tenant on the platform: company, owner, status, created, basic usage.
// A tenant token can never reach this data (the whole /admin area is behind the platform guard).

const STATUS_CHIP: Record<CustomerStatus, { cls: string; label: string }> = {
  active: { cls: 'green', label: 'פעיל' },
  suspended: { cls: 'gold', label: 'מושהה' },
  cancelled: { cls: 'muted', label: 'בוטל' },
};

function dateHe(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function CustomersPage(): JSX.Element {
  const navigate = useNavigate();
  const { data: customers, isLoading } = useQuery({ queryKey: ['admin', 'customers'], queryFn: adminApi.customers });

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <div className="eyebrow">קונסולת ניהול</div>
          <h1>לקוחות</h1>
          <div className="sub">כל הלקוחות בפלטפורמה — סטטוס, בעלים ושימוש</div>
        </div>
        <button className="btn" onClick={() => navigate('/admin/customers/new')}><Icon name="plus" /> לקוח חדש</button>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {isLoading || !customers ? (
          <div className="empty">טוען…</div>
        ) : customers.length === 0 ? (
          <div className="empty">עדיין אין לקוחות. לחץ "לקוח חדש" כדי להוסיף את הראשון.</div>
        ) : (
          <table className="dtable">
            <thead>
              <tr><th>חברה</th><th>בעלים</th><th>סטטוס</th><th>מספרים</th><th>שידורים (30 יום)</th><th>נוצר</th></tr>
            </thead>
            <tbody>
              {customers.map((c) => {
                const chip = STATUS_CHIP[c.status];
                return (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/customers/${c.id}`)}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ fontSize: 13 }}>
                      <span className="ltr" style={{ color: 'var(--text-dim)' }}>{c.owner?.email ?? '—'}</span>
                      {c.owner && !c.owner.activated && (
                        <span className="chip gold" style={{ marginInlineStart: 8 }}>טרם הופעל</span>
                      )}
                    </td>
                    <td><span className={`chip ${chip.cls}`}>{chip.label}</span></td>
                    <td className="mono">{fmt(c.usage.numbers)}</td>
                    <td className="mono">{fmt(c.usage.campaigns30d)}</td>
                    <td className="mono" style={{ color: 'var(--text-dim)', fontSize: 12.5 }}>{dateHe(c.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
